import { Injectable } from '@angular/core';

export interface ParsedStep {
  placeholder: string;
  additionalInfo: { [key: string]: string };
}

@Injectable({
  providedIn: 'root'
})
export class StepPlaceholderService {

  /**
   * Parse step description to extract placeholder and values
   * Example: "Enter username as test" → placeholder: "Enter username as {{value}}", additionalInfo: {"value": "test"}
   * Example: "Type admin in username" → placeholder: "Type {{value}} in username", additionalInfo: {"value": "admin"}
   */
  parseStepDescription(stepDescription: string): ParsedStep {
    const trimmed = stepDescription.trim();
    
    // Pattern 1: "action target as value" or "action target as 'value'"
    // Examples: "Enter username as test", "Type password as Test@123"
    const asPattern = /^(.*?)\s+as\s+(.+)$/i;
    const asMatch = trimmed.match(asPattern);
    
    if (asMatch) {
      const beforeAs = asMatch[1].trim();
      const value = asMatch[2].trim().replace(/^['"]|['"]$/g, ''); // Remove quotes if present
      
      return {
        placeholder: `${beforeAs} as {{value}}`,
        additionalInfo: { value }
      };
    }

    // Pattern 2: "action value in target" or "Type value in target"
    // Examples: "Type admin in username", "Enter Test@123 in password"
    const typeInPattern = /^(type|enter)\s+(.+?)\s+in\s+(.+)$/i;
    const typeInMatch = trimmed.match(typeInPattern);
    
    if (typeInMatch) {
      const action = typeInMatch[1];
      const value = typeInMatch[2].trim().replace(/^['"]|['"]$/g, '');
      const target = typeInMatch[3].trim();
      
      return {
        placeholder: `${action} {{value}} in ${target}`,
        additionalInfo: { value }
      };
    }

    // Pattern 3: "Navigate to url" 
    // Example: "Navigate to https://example.com"
    const navigatePattern = /^navigate\s+to\s+(.+)$/i;
    const navigateMatch = trimmed.match(navigatePattern);
    
    if (navigateMatch) {
      const url = navigateMatch[1].trim();
      
      return {
        placeholder: `Navigate to {{url}}`,
        additionalInfo: { url }
      };
    }

    // Pattern 4: "Select option from dropdown"
    // Example: "Select 'United States' from country dropdown"
    const selectPattern = /^select\s+['"]?(.+?)['"]?\s+from\s+(.+)$/i;
    const selectMatch = trimmed.match(selectPattern);
    
    if (selectMatch) {
      const option = selectMatch[1].trim();
      const target = selectMatch[2].trim();
      
      return {
        placeholder: `Select {{option}} from ${target}`,
        additionalInfo: { option }
      };
    }

    // Pattern 5: "Wait N seconds" 
    // Example: "Wait 3 seconds", "Wait 5 seconds"
    const waitPattern = /^wait\s+(\d+)\s+seconds?$/i;
    const waitMatch = trimmed.match(waitPattern);
    
    if (waitMatch) {
      const duration = waitMatch[1];
      
      return {
        placeholder: `Wait {{duration}} seconds`,
        additionalInfo: { duration }
      };
    }

    // Pattern 6: "Verify target contains/equals value"
    // Example: "Verify page contains 'Welcome'", "Verify title equals 'Dashboard'"
    const verifyPattern = /^verify\s+(.+?)\s+(contains|equals|includes)\s+['"]?(.+?)['"]?$/i;
    const verifyMatch = trimmed.match(verifyPattern);
    
    if (verifyMatch) {
      const target = verifyMatch[1].trim();
      const condition = verifyMatch[2].trim();
      const expectedValue = verifyMatch[3].trim();
      
      return {
        placeholder: `Verify ${target} ${condition} {{expectedValue}}`,
        additionalInfo: { expectedValue }
      };
    }

    // No pattern matched - return original step (no placeholder needed)
    return {
      placeholder: '',
      additionalInfo: {}
    };
  }

  /**
   * Resolve placeholder by replacing {{key}} with actual values from additionalInfo
   * Example: placeholder: "Enter username as {{value}}", additionalInfo: {"value": "test"} → "Enter username as test"
   */
  resolvePlaceholder(placeholder: string, additionalInfo: { [key: string]: string }): string {
    if (!placeholder) {
      return '';
    }

    let resolved = placeholder;
    
    // Replace all {{key}} with values from additionalInfo
    Object.keys(additionalInfo).forEach(key => {
      const pattern = new RegExp(`{{${key}}}`, 'g');
      resolved = resolved.replace(pattern, additionalInfo[key]);
    });

    return resolved;
  }

  /**
   * Parse additionalInfo JSON string safely
   */
  parseAdditionalInfo(additionalInfoStr: string): { [key: string]: string } {
    try {
      const parsed = JSON.parse(additionalInfoStr);
      // Filter out non-value fields (like 'type')
      const { type, ...values } = parsed;
      return values;
    } catch (error) {
      return {};
    }
  }
}
